import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import TableView from "./TableView";
import CardView from "./CardView";
import "./PageLayout.css";

export default function PageLayout({
  records,
  loading,
  hideSort = false,
  topBarClassName = "view-sort-row",
  leftClassName = "view-toggle-container",
  centerClassName = "centered-search",
  rightClassName = "sort-container",
  podcastName = "",
  sortMode = "best",
  setSortMode
}) {
  const location = useLocation();
  // Search functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortField, setSortField] = useState("Date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Determine view mode based on podcast name or URL
  const isShamelessFromName = podcastName.toLowerCase().includes("shameless");
  const isShamelessFromUrl = location.pathname.toLowerCase().includes("shameless");
  const isShameless = isShamelessFromName || isShamelessFromUrl;
  const defaultViewMode = isShameless ? "table" : "card";
  const [viewMode, setViewMode] = useState(defaultViewMode);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);



  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    let parsedDate = new Date(dateString);
    if (isNaN(parsedDate)) parsedDate = new Date(dateString.replace(/-/g, "/"));
    return isNaN(parsedDate)
      ? ""
      : parsedDate.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const filteredRecords = records
    .filter((record) => {
      if (!debouncedSearchTerm) return true;
      
      const searchLower = debouncedSearchTerm.toLowerCase();
      const recommendation = (record.fields?.Recommendation || "").toLowerCase();
      const recommenderName = (record.fields?.recommenderName || "").toLowerCase();
      
      return recommendation.includes(searchLower) || 
             recommenderName.includes(searchLower);
    })
    .sort((a, b) => {
      const aVal = a.fields?.[sortField] || "";
      const bVal = b.fields?.[sortField] || "";
      return sortDirection === "asc"
        ? aVal > bVal ? 1 : -1
        : aVal < bVal ? 1 : -1;
    });

  // Pagination calculations
  const totalItems = filteredRecords.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  // Reset to page 1 when sort mode changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortMode]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    // For Shameless podcast, always use table view
    // For other podcasts, always use card view
    if (isShameless) {
      setViewMode("table");
    } else {
      setViewMode("card");
    }
  }, [isShameless]);

  return (
    <div className="podcast-page">
      <div className="filter-container">
        <div className="filter-inner">
          {records.length > 0 && (
            <div className={topBarClassName}>
              <div className={leftClassName}>
                {/* View toggle buttons removed - each podcast has a fixed view mode */}
              </div>

              <div className={centerClassName}>
                <input
                  type="text"
                  placeholder="Search recommendations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {!hideSort && isShameless && (
                <div className={rightClassName}>
                  <div className="sort-buttons">
                    <button 
                      className={`sort-toggle-btn ${sortMode === 'best' ? 'active' : ''}`}
                      onClick={() => setSortMode && setSortMode('best')}
                    >
                      Popular
                    </button>
                    <button 
                      className={`sort-toggle-btn ${sortMode === 'date' ? 'active' : ''}`}
                      onClick={() => setSortMode && setSortMode('date')}
                    >
                      Date
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : filteredRecords.length === 0 ? (
        <div className="empty-page-message">
          {debouncedSearchTerm ? (
            <p>No recommendations found matching "{debouncedSearchTerm}".</p>
          ) : (
            <p>No recommendations found.</p>
          )}
        </div>
      ) : (
        <>
          {debouncedSearchTerm && (
            <div className="results-summary">
              Found {filteredRecords.length} recommendation{filteredRecords.length !== 1 ? 's' : ''} matching "{debouncedSearchTerm}"
            </div>
          )}
          <div className="content">

            {viewMode === "table" ? (
            <TableView
              key={`table-${sortMode}-${currentPage}`}
              filteredRecords={paginatedRecords}
              formatDate={formatDate}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
              sortMode={sortMode}
            />
          ) : (
            <CardView
              filteredRecords={paginatedRecords}
              formatDate={formatDate}
            />
          )}

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first page, last page, current page, and pages around current
                    return page === 1 || 
                           page === totalPages || 
                           Math.abs(page - currentPage) <= 2;
                  })
                  .map((page, index, array) => {
                    // Add ellipsis if there's a gap
                    const showEllipsis = index > 0 && page - array[index - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && <span className="pagination-ellipsis">...</span>}
                        <button
                          className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
}
